import { computed, reactive, ref } from 'vue'
import * as Sentry from '@sentry/nuxt'
import { toast } from 'vue-sonner'
import { useInteractions, InteractionResponseType, type InteractionResult } from '~/composables/useInteractions'
import { parseModalJson, initialModalValues, type ParsedModal } from '~/types/messageComponents'
import { useMessagesStore } from '~/stores/messages'

export interface InteractionContext {
  botId: string
  channelId: string
  groupId?: string | null
}

// Module-level singleton (same pattern as `useMessageEditing`): a modal opened from a slash-command
// response (ChatMessageInput.vue) and one opened from clicking a component on an arbitrary message
// in the list (MessageComponents.vue, one instance per message) both need the same single dialog —
// only one interaction modal can ever be open at a time, so there's nothing to gain from each
// component owning its own copy of this state.
const activeModal = ref<ParsedModal | null>(null)
const modalContext = ref<InteractionContext | null>(null)
const modalValues = reactive<Record<string, string>>({})
const isSubmitting = ref(false)

export function useInteractionModal() {
  const { submitModal } = useInteractions()

  function openFromResult(result: InteractionResult, context: InteractionContext) {
    const modal = parseModalJson(result.modal_json)
    if (!modal) return
    activeModal.value = modal
    modalContext.value = context
    for (const key of Object.keys(modalValues)) delete modalValues[key]
    Object.assign(modalValues, initialModalValues(modal))
  }

  function cancel() {
    activeModal.value = null
    modalContext.value = null
  }

  const canSubmit = computed(() => {
    const modal = activeModal.value
    if (!modal) return false
    return modal.components.every((row) =>
      row.components.every((c) => c.type !== 'textInput' || !c.required || modalValues[c.customId]?.trim()),
    )
  })

  async function submit(onResult: (result: InteractionResult, context: InteractionContext) => void) {
    const modal = activeModal.value
    const context = modalContext.value
    if (!modal || !context || !canSubmit.value || isSubmitting.value) return

    isSubmitting.value = true
    try {
      const result = await submitModal({
        bot_id: context.botId,
        channel_id: context.channelId,
        group_id: context.groupId ?? null,
        custom_id: modal.customId,
        fields: Object.entries(modalValues).map(([name, value]) => ({ name, value })),
      })
      activeModal.value = null
      modalContext.value = null
      onResult(result, context)
    } catch (error: any) {
      console.error('Failed to submit modal', error)
      Sentry.captureException(error, { extra: { action: 'submit_modal', customId: modal.customId } })
      toast.error(error?.message || 'Не удалось отправить форму')
    } finally {
      isSubmitting.value = false
    }
  }

  return { activeModal, modalValues, isSubmitting, canSubmit, openFromResult, cancel, submit }
}

/**
 * Shared outcome handling for every interaction entry point (slash commands, message components,
 * modal submissions): opens the next modal if the bot chained into one, otherwise shows an ephemeral
 * response as a «visible only to you» message in the chat (it's only ever visible to the invoker and never becomes a real persisted message —
 * see `docs/bots.md`). A non-ephemeral `CHANNEL_MESSAGE`/`UPDATE_MESSAGE`/deferred result needs no
 * handling here at all: the bot's actual message arrives through the normal
 * `MessageCreated`/`MessageUpdated` realtime event, same path as any other message.
 */
export function handleInteractionResult(result: InteractionResult, context: InteractionContext) {
  if (result.type === InteractionResponseType.MODAL) {
    useInteractionModal().openFromResult(result, context)
    return
  }
  if (result.ephemeral && result.content) {
    // Discord's ephemeral message: in the chat, but only for the invoker and never persisted.
    useMessagesStore().addEphemeral({
      id: crypto.randomUUID(),
      channel_id: context.channelId,
      author_id: context.botId,
      content: result.content,
      reply_to: null,
      quick_replies: [],
      components_json: result.components_json || null,
      created_at: new Date().toISOString(),
    })
  }
}
